import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Signal,
} from '@angular/core';

import { Router } from '@angular/router';

import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

import { Account, SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import { ConfigurationService } from 'app/providers/semantria-api/configuration.service';
import { AccountCounts } from 'app/providers/storage-api/blender-account.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import { AlertService } from 'app/services/alert.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';

import {
  EvaluationService,
  SemantriaVersions,
} from '../../../../providers/semantria-api/evaluation-service';
import { LocalStorageService } from '../../../../services/local-storage.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-account-dropdown-wrapper',
  templateUrl: './account-dropdown-wrapper.component.html',
  styleUrls: ['./account-dropdown-wrapper.component.scss'],
  animations: [
    trigger('arrowState', [
      state(
        'closed',
        style({
          transform: 'rotate(0deg)',
        })
      ),
      state(
        'open',
        style({
          transform: 'rotate(180deg)',
        })
      ),
      transition('closed <=> open', animate(300)),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountDropdownWrapperComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  readonly session = input<Session>();
  readonly accountCounts = input<AccountCounts>();
  readonly account = input<Account>();
  readonly appVersion = input<any>();
  readonly userTimezone = input<string>();

  readonly homepageChanged = output<string>();
  readonly openChangeTimezone = output<void>();
  readonly openChangeLanguage = output<void>();

  public userName = computed(() => {
    const session = this.session();
    if (session) {
      if (session.user.first_name || session.user.last_name) {
        return `${session.user.first_name} ${session.user.last_name}`;
      } else {
        return session.user.user_name;
      }
    }
    return '...';
  });
  public userInitials = computed(() => {
    const session = this.session();
    if (session) {
      const fn = session.user.first_name;
      const ln = session.user.last_name;
      const email = session.user.email_address;
      if (fn && ln) {
        return fn.substring(0, 1) + ln.substring(0, 1);
      } else if (ln) {
        return ln.substring(0, 1);
      } else if (email) {
        return email.substring(0, 1);
      }
    }
    return '';
  });
  public userLogin = computed(() => {
    return this.session()?.user.user_name ?? '';
  });

  dropdownOpen = false;
  state = 'closed';

  isSSOUser: boolean;
  showPasswordModal = false;
  semantriaVersions: SemantriaVersions;
  driverAnalysisEnabled = false;
  driverAnalysisBalance = '-1';
  translationEnabled = false;
  translationBalance = '-1';
  spoteScenario = '0';
  accountCountsSafe: Signal<{ [k in keyof AccountCounts]: string }> = computed(() => {
    return {
      documentCount: (this.accountCounts()?.documentCount ?? 0).toLocaleString(),
      documentLimit: (this.accountCounts()?.documentLimit ?? 0).toLocaleString(),
      semantriaCredits: (this.accountCounts()?.semantriaCredits ?? 0).toLocaleString(),
      projectsCount: (this.accountCounts()?.projectsCount ?? 0).toLocaleString(),
      projectsLimit: (this.accountCounts()?.projectsLimit ?? 0).toLocaleString(),
    };
  });

  private semantriaAccountSubscription: Subscription;
  public localStorageService = inject(LocalStorageService);

  constructor(
    public authService: AuthService,
    private router: Router,
    private preferencesService: SemantriaPreferences,
    private alertService: AlertService,
    private configService: ConfigurationService,
    private evaluationService: EvaluationService,
    private systemService: SystemService,
    private semantriaAccount: SemantriaAccount,
    private translocoService: TranslocoService,
    private collectionService: CollectionService
  ) {}

  ngOnInit() {
    this.evaluationService.getVersions().then((v) => (this.semantriaVersions = v));
    this.isSSOUser = this.authService.isSSOUser;
    this.semantriaAccountSubscription = this.semantriaAccount.sub.subscribe((account) => {
      if (!account) {
        return;
      }

      this.driverAnalysisEnabled = account.features.includes('DRIVER_ANALYSIS');
      if (this.driverAnalysisEnabled) {
        this.driverAnalysisBalance = (account.balances?.DRIVER_ANALYSES ?? 0).toLocaleString();
      }
      this.translationEnabled = account.features.includes('TRANSLATION');
      if (this.translationEnabled) {
        this.translationBalance = (
          account.balances?.current_translation_balance ||
          account.current_translation_balance ||
          -1
        ).toLocaleString();
        if (this.translationBalance === '-1') {
          // temp hack until the balance gets returned
          this.translationEnabled = false;
        }
      }

      this.spoteScenario = (account.balances.SPOT_E_SCENARIO ?? 0).toLocaleString();
    });
  }

  ngOnDestroy(): void {
    this.semantriaAccountSubscription.unsubscribe();
  }

  public onOpenChangePassword() {
    this.showPasswordModal = true;
  }

  public onPasswordModalClosed() {
    this.showPasswordModal = false;
  }

  public setHome(): void {
    const homepage = this.router.url;
    this.preferencesService
      .savePreference('homepage', homepage)
      .then(() => {
        this.homepageChanged.emit(homepage);
        this.alertService.showToast(
          this.translocoService.translate('account-dropdown-wrapper.homepage-preference-saved')
        );
      })
      .catch((error) => {
        this.alertService.showErrorToast(
          this.translocoService.translate(
            'account-dropdown-wrapper.homepage-preference-failed-to-save'
          ),
          error
        );
      });
  }

  public changeTimezone(): void {
    this.openChangeTimezone.emit();
  }

  onChangeLanguage(): void {
    this.openChangeLanguage.emit();
  }

  public clearEvalCache(): void {
    this.collectionService.clearUserCache();
    this.localStorageService.removeItem('evaluationConfigMap');
  }

  public async logout() {
    this.configService.clearEvaluationConfigs();
    await this.authService.logout();
  }

  onToggleDropdown(isClosed: boolean) {
    this.dropdownOpen = !isClosed;
    this.state = this.state === 'closed' ? 'open' : 'closed';
  }

  onCopyToClipboard($event, text: string) {
    $event.detail.nativeEvent.stopImmediatePropagation();
    this.systemService.copyToClipboard(text);
    this.alertService.showToast(
      this.translocoService.translate('account-dropdown-wrapper.text-has-been-copied-to-clipboard')
    );
  }
}
